import type { UniqueIdentifier } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cx from "classnames";
import {
  type CSSProperties,
  type ElementType,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useRef,
} from "react";

import S from "./Sortable.module.css";

export interface SortableProps {
  id: UniqueIdentifier;
  as?: ElementType;
  children:
    | ReactNode
    | ((data: {
        dragHandleRef: MutableRefObject<HTMLElement | null>;
        dragHandleListeners: SyntheticListenerMap | undefined;
      }) => ReactNode);
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  draggingStyle?: CSSProperties;
  role?: string;
}

/**
 * Wrapper to use with dnd-kit's Sortable preset
 * https://docs.dndkit.com/presets/sortable
 */
export function Sortable({
  id,
  as: Component = "div",
  children,
  disabled = false,
  className,
  style,
  draggingStyle,
  role = "button",
}: SortableProps) {
  const dragHandleRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({
    id,
    disabled,
    animateLayoutChanges: () => false,
  });

  useEffect(() => {
    if (dragHandleRef?.current) {
      setActivatorNodeRef(dragHandleRef.current);
    }
  }, [dragHandleRef, setActivatorNodeRef]);

  const childrenAsFunction = typeof children === "function";

  // dnd-kit's own `attributes` (role: "button", tabIndex: 0, plus
  // aria-roledescription/aria-describedby pointing at its own "how to drag
  // this with a keyboard" screen-reader copy) assume this element itself is
  // the focusable, keyboard-operable drag handle. A caller that overrides
  // `role` to a non-widget role (e.g. "listitem", "presentation" — this
  // element is just structural, with its own separate interactive content,
  // or the real drag handle lives elsewhere via `dragHandleRef`) doesn't
  // want any of that: keeping it produced a focusable element with an
  // invalid widget role (WCAG 2.1.1, element_tabbable_role_valid). None of
  // this app's `DndContext`s register a `KeyboardSensor` for `Sortable`-
  // based drag (only `MouseSensor`/`TouchSensor`, see `useDndSensors`), so
  // that tabIndex was never wired to a working keyboard interaction to
  // begin with — omitting `attributes` here doesn't remove any real
  // functionality.
  const isWidgetRole = role === "button";

  return (
    <Component
      className={cx(className, { [S.dragging]: isDragging })}
      style={{
        ...style,
        ...(isDragging ? draggingStyle : {}),
        transform: CSS.Translate.toString(transform),
        transition: transition,
      }}
      data-is-dragging={isDragging}
      ref={setNodeRef}
      {...(disabled || !isWidgetRole ? {} : attributes)}
      {...(!childrenAsFunction && listeners)}
      role={role}
    >
      {childrenAsFunction
        ? children({ dragHandleRef, dragHandleListeners: listeners })
        : children}
    </Component>
  );
}
